/**
 * Minimal drop-in replacement for the abandoned `react-localization` package.
 *
 * The codebase only ever used the English locale (all objects are `{ en: {...} }`),
 * so this shim simply unwraps the `en` property and exposes a `formatString`
 * utility that replicates the two calling conventions the original provided:
 *   - Named:      formatString('Hello {name}', { name: 'World' })
 *   - Positional: formatString('Hello {0}', 'World')
 */

export type GlobalStrings<T> = { en: T };

export function LocalizedStrings<T extends Record<string, unknown>>(
  translation: GlobalStrings<T>,
): T & { formatString<S = string>(template: string | unknown, ...params: (Record<string, unknown> | string | number)[]): S } {
  function formatString<S = string>(
    template: string | unknown,
    ...params: (Record<string, unknown> | string | number)[]
  ): S {
    const str = typeof template === 'string' ? template : String(template ?? '');
    if (params.length === 0) return str as unknown as S;

    // Named format: single object arg → replace {key} placeholders
    if (params.length === 1 && typeof params[0] === 'object' && params[0] !== null) {
      const named = params[0] as Record<string, unknown>;
      const result = Object.entries(named).reduce(
        (s, [key, val]) => s.replace(new RegExp(`\\{${key}\\}`, 'g'), String(val ?? '')),
        str,
      );
      return result as unknown as S;
    }

    // Positional format: each extra arg replaces {0}, {1}, …
    const result = params.reduce<string>(
      (s, val, i) => s.replace(new RegExp(`\\{${i}\\}`, 'g'), String(val ?? '')),
      str,
    );
    return result as unknown as S;
  }

  return { ...translation.en, formatString } as T & { formatString: typeof formatString };
}
